import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ROUTE_DEFINITIONS } from '@/routes';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useUI } from '@/hooks/ui';
import { useUnsavedChangesWarning } from '@/hooks/ui/navigation';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import { employeeContractCreateService } from '@/api/domains/employeeContract/services/employeeContractCreate.service';
import { employeeGet, type EmployeeProfile } from '@/api/domains/employee';
import { mapCreateEmployeeContractFormToTransport } from '@/api/domains/employeeContract/mappers/employeeContractCreate.mapper';
import type { ValidatedCreateEmployeeContractFormData } from '@/api/domains/employeeContract/types/employeeContractCreate.types';
import {
  createEmployeeContractSchema,
  type CreateEmployeeContractFormData,
} from '@/schemas/employee/employeeContractCreate.schema';
import {
  useHoldingCompanies,
  useContractStatuses,
  useContractTypes,
  useContractModalities,
  useAreas,
  usePositions,
  useCostCenters,
  useUnions,
} from '@/api/domains/catalog';


interface UseEmployeeContractCreateResult {
  isLoading: boolean;
  creating: boolean;
  employeeProfile: EmployeeProfile | null;
  handleCancel: () => void;
  handleCreate: () => Promise<void>;
  contractStatusOptions: { value: string; label: string }[];
  contractTypeOptions: { value: string; label: string }[];
  contractModalityOptions: { value: string; label: string }[];
  companyOptions: { value: string; label: string }[];
  areaOptions: { value: string; label: string }[];
  positionOptions: { value: string; label: string }[];
  costCenterOptions: { value: string; label: string }[];
  unionOptions: { value: string; label: string }[];
  form: {
    register: ReturnType<
      typeof useForm<CreateEmployeeContractFormData>
    >['register'];
    setValue: ReturnType<
      typeof useForm<CreateEmployeeContractFormData>
    >['setValue'];
    watch: ReturnType<
      typeof useForm<CreateEmployeeContractFormData>
    >['watch'];
    errors: ReturnType<
      typeof useForm<CreateEmployeeContractFormData>
    >['formState']['errors'];
    trigger: ReturnType<
      typeof useForm<CreateEmployeeContractFormData>
    >['trigger'];
  };
}


export function useEmployeeContractCreate(employeeId?: number): UseEmployeeContractCreateResult {
  const navigate = useNavigate();
  const { showNotification } = useUI();
  const { t: translate } = useTranslation();
  const [creating, setCreating] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [employeeProfile, setEmployeeProfile] = useState<EmployeeProfile | null>(null);
  const {
    companies: holdingCompanies,
    loading: loadingCompanies,
  } = useHoldingCompanies();
  const {
    statuses: contractStatuses,
    loading: loadingStatuses,
  } = useContractStatuses();
  const {
    types: contractTypes,
    loading: loadingTypes,
  } = useContractTypes();
  const {
    modalities: contractModalities,
    loading: loadingModalities,
  } = useContractModalities();
  const { areas, loading: loadingAreas } = useAreas();
  const { costCenters, loading: loadingCostCenters } = useCostCenters();
  const { unions, loading: loadingUnions } = useUnions();

  // React Hook Form instance
  const formMethods = useForm<CreateEmployeeContractFormData>({
    resolver: zodResolver(createEmployeeContractSchema),
    mode: 'onBlur',
    defaultValues: {
      contractNumber: '',
      contractName: '',
      contractStatusId: 10, // Default: Activo
      contractTypeId: undefined,
      dateEntry: undefined,
      contractStartDate: undefined,
      contractEndDate: null,
      contractModalityId: undefined,
      companyContractId: undefined,
      areaId: undefined,
      positionId: undefined,
      costCenterId: undefined,
      unionCode: '',
    },
  });

  // Watch areaId to reload positions when area changes
  const selectedAreaId = formMethods.watch('areaId');
  const { positions, loading: _loadingPositions } = usePositions(selectedAreaId);

  // Load employee profile on mount
  useEffect(() => {
    if (!employeeId) {
      setLoadingProfile(false);
      return;
    }

    (async () => {
      setLoadingProfile(true);
      try {
        const response = await employeeGet.getProfile(employeeId);
        if (response.success && response.data) {
          setEmployeeProfile(response.data);
        }
      } catch {
        // Silently fail - profile is optional for contract creation
      } finally {
        setLoadingProfile(false);
      }
    })().catch(() => {
      setLoadingProfile(false);
    });
  }, [employeeId]);

  // Auto-select first position when positions are loaded for the selected area
  useEffect(() => {
    if (selectedAreaId && positions.length > 0) {
      formMethods.setValue('positionId', positions[0].id, { shouldValidate: false });
    } else if (!selectedAreaId) {
      formMethods.setValue('positionId', undefined as unknown as number, { shouldValidate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAreaId, positions]);

  // Combine all loading states - wait for all catalogs to finish
  const loading = 
    loadingProfile ||
    loadingCompanies || 
    loadingStatuses || 
    loadingTypes || 
    loadingModalities || 
    loadingAreas || 
    loadingCostCenters || 
    loadingUnions;

  // Navigation target for cancel
  const navigateToList = useCallback(() => {
    const baseUrl = ROUTE_DEFINITIONS.EMPLEADO_CONTRATOS.path;
    const url = employeeId ? `${baseUrl}?employeeId=${employeeId}` : baseUrl;
    void navigate(url);
  }, [navigate, employeeId]);

  // Calculate isDirty state from form
  const isDirty = formMethods.formState.isDirty;

  // Unsaved changes warning hook
  const { handleCancelWithWarning: handleCancel, confirmNavigation } = useUnsavedChangesWarning({
    onConfirmLeave: navigateToList,
    isDirty,
  });

  const handleCreate = useCallback(async () => {
    const isValid = await formMethods.trigger();
    if (!isValid) {
      showNotification(
        'error',
        translate('common:validation.formErrors'),
        5000,
      );
      return;
    }

    setCreating(true);

    try {
      const formData = formMethods.getValues();
      const employeeIdNum = Number(employeeId);
      
      // Add employeeId from params and convert contractNumber to number
      const formDataWithEmployee: ValidatedCreateEmployeeContractFormData =
        {
          ...formData,
          employeeId: employeeIdNum,
          contractNumber: Number(formData.contractNumber),
          contractStatusId: formData.contractStatusId,
          contractTypeId: formData.contractTypeId,
          dateEntry: formData.dateEntry,
          contractStartDate: formData.contractStartDate,
          contractEndDate:
          formData.contractEndDate ?? null,
          contractModalityId: formData.contractModalityId,
          companyContractId: formData.companyContractId,
          positionId: formData.positionId,
          costCenterId: formData.costCenterId,
          areaId: formData.areaId,
          unionCode: formData.unionCode,
        };

      const transportData =
        mapCreateEmployeeContractFormToTransport(formDataWithEmployee);
      const response = await employeeContractCreateService.create(
        employeeIdNum,
        transportData,
      );

      if (response.success && response.data) {
        showNotification(
          'success',
          translate(
            'employee:contracts.messages.created',
          ),
          5000,
        );
        // Mark navigation as confirmed to bypass unsaved changes warning
        confirmNavigation();
        const baseUrl = ROUTE_DEFINITIONS.EMPLEADO_CONTRATOS.path;
        const successUrl = employeeId
          ? `${baseUrl}?employeeId=${employeeId}`
          : baseUrl;
        void navigate(successUrl);
      } else if (response.error) {
        const errorMessage = response.error.detail ||
          translate('common:errors.unexpected');

        // Process backend validation errors if available
        if (response.error.validationErrors) {
          injectValidationErrors(response.error.validationErrors, formMethods);
        }
        
        showNotification('error', errorMessage, 5000);
      }
    } catch (catchError) {
      const errorMessage = catchError instanceof Error
        ? catchError.message
        : translate('common:errors.unexpected');
      showNotification('error', errorMessage, 5000);
    } finally {
      setCreating(false);
    }
  }, [
    formMethods, employeeId, showNotification, translate, navigate, confirmNavigation,
  ]);

  return {
    isLoading: loading,
    creating,
    employeeProfile,
    handleCancel,
    handleCreate,
    contractStatusOptions: contractStatuses.map((status) => ({
      value: String(status.id),
      label: status.name,
    })),
    contractTypeOptions: contractTypes.map((type) => ({
      value: String(type.id),
      label: type.name,
    })),
    contractModalityOptions: contractModalities.map((modality) => ({
      value: String(modality.id),
      label: modality.name,
    })),
    companyOptions: holdingCompanies.map((company) => ({
      value: String(company.id),
      label: company.name,
    })),
    areaOptions: areas.map((area) => ({
      value: String(area.id),
      label: area.name,
    })),
    positionOptions: positions.map((position) => ({
      value: String(position.id),
      label: position.name,
    })),
    costCenterOptions: costCenters.map((costCenter) => ({
      value: String(costCenter.id),
      label: costCenter.name,
    })),
    unionOptions: unions.map((union) => ({
      value: union.id,
      label: union.name,
    })),
    form: {
      register: formMethods.register,
      setValue: formMethods.setValue,
      watch: formMethods.watch,
      errors: formMethods.formState.errors,
      trigger: formMethods.trigger,
    },
  };
}
